import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Bell, Zap, Shield, BarChart3, ArrowRight, Check, Layout as LayoutIcon, MousePointer2, Layers, Globe, CreditCard, Mail, MessageSquare, Lock, Menu, X, Twitter, Linkedin, Instagram } from 'lucide-react';
interface AgentToBossProps {
  onBack?: () => void;
}
export function AgentToBoss({
  onBack
}: AgentToBossProps) {
  const [activeFeature, setActiveFeature] = useState('contacts');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [view, setView] = useState<'home' | 'domains' | 'platform' | 'demo' | 'enterprise' | 'pricing' | 'client-dashboard' | 'crm-core' | 'automations' | 'analytics' | 'integrations' | 'about' | 'careers' | 'press' | 'contact' | 'privacy' | 'terms' | 'security'>('home');
  const [activeDashboardTab, setActiveDashboardTab] = useState('dashboard');
  // Scroll to top on view change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [view]);
  // Disable scroll when modal is open
  useEffect(() => {
    if (videoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [videoModalOpen]);
  const features = [{
    id: 'contacts',
    title: 'Contact Management',
    desc: 'Centralize every interaction. Our intelligent CRM automatically enriches lead data and tracks communication history across all channels.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'leads',
    title: 'Lead Tracking',
    desc: 'Visualize your funnel with precision. Move leads through custom stages and let AI predict conversion probability based on engagement.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'reminders',
    title: 'Instant Reminders',
    desc: 'Never miss a follow-up. Agentic triggers notify you exactly when a lead is most likely to engage, ensuring maximum ROI on every touchpoint.',
    image: 'https://images.unsplash.com/photo-1551288049-bbbda5366392?auto=format&fit=crop&q=80&w=1200'
  }];
  return <div className="min-h-screen bg-[#F3F6F9] text-[#1B264F] font-sans selection:bg-[#00A1E0] selection:text-white" data-id="element-1743">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#D8E2EF] px-8 py-4" data-id="element-1744">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1745">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => {
          setView('home');
          setMobileMenuOpen(false);
        }} data-id="element-1746">
            <div className="w-10 h-10 bg-[#00A1E0] rounded-lg flex items-center justify-center text-white shadow-lg shadow-[#00A1E0]/20" data-id="element-1747">
              <Zap size={24} fill="currentColor" data-id="element-1748" />
            </div>
            <span className="text-xl font-black tracking-tight text-[#1B264F]" data-id="element-1749">
              Agent<span className="text-[#00A1E0]" data-id="element-1750">to</span>boss
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-bold text-[#1B264F]" data-id="element-1751">
            <button onClick={() => setView('home')} className={`hover:text-[#00A1E0] transition-colors ${view === 'home' ? 'text-[#00A1E0]' : ''}`} data-id="element-1752">
              Home
            </button>
            <button onClick={() => setView('platform')} className={`hover:text-[#00A1E0] transition-colors ${view === 'platform' ? 'text-[#00A1E0]' : ''}`} data-id="element-1753">
              Platform
            </button>
            <button onClick={() => setView('domains')} className={`hover:text-[#00A1E0] transition-colors ${view === 'domains' ? 'text-[#00A1E0]' : ''}`} data-id="element-1754">
              Domains & Hosting
            </button>
            <button onClick={() => setView('enterprise')} className={`hover:text-[#00A1E0] transition-colors ${view === 'enterprise' ? 'text-[#00A1E0]' : ''}`} data-id="element-1755">
              Enterprise
            </button>
            <button onClick={() => setView('pricing')} className={`hover:text-[#00A1E0] transition-colors ${view === 'pricing' ? 'text-[#00A1E0]' : ''}`} data-id="element-1756">
              Pricing
            </button>
          </div>

          <div className="flex items-center gap-4" data-id="element-1757">
            {onBack && <button onClick={onBack} className="hidden md:block text-xs font-bold uppercase tracking-widest text-[#1B264F]/60 hover:text-[#1B264F] transition-colors mr-4" data-id="element-1758">
                ← Back to Admin
              </button>}
            <button onClick={() => setView('client-dashboard')} className="hidden md:block px-6 py-2.5 border-2 border-[#00A1E0] text-[#00A1E0] rounded-full text-sm font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-1759">
              Client Login
            </button>
            <button onClick={() => setView('demo')} className="hidden md:block px-6 py-2.5 bg-[#00A1E0] text-white rounded-full text-sm font-bold hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-1760">
              Demo
            </button>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#1B264F] hover:bg-[#F3F6F9] rounded-xl transition-colors" data-id="element-1761">
              {mobileMenuOpen ? <X size={24} data-id="element-1762" /> : <Menu size={24} data-id="element-1763" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-1764">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="md:hidden absolute top-full left-0 w-full bg-white border-t border-[#D8E2EF] px-8 py-8 space-y-6 shadow-2xl z-50" data-id="element-1765">
              <div className="flex flex-col gap-4 text-lg font-bold text-[#1B264F]" data-id="element-1766">
                {[{
              id: 'home',
              label: 'Home'
            }, {
              id: 'platform',
              label: 'Platform'
            }, {
              id: 'domains',
              label: 'Domains & Hosting'
            }, {
              id: 'enterprise',
              label: 'Enterprise'
            }, {
              id: 'pricing',
              label: 'Pricing'
            }].map(item => <button key={item.id} onClick={() => {
              setView(item.id as any);
              setMobileMenuOpen(false);
            }} className={`text-left hover:text-[#00A1E0] transition-colors ${view === item.id ? 'text-[#00A1E0]' : ''}`} data-id="element-1767">
                    {item.label}
                  </button>)}
              </div>

              <div className="pt-6 border-t border-[#D8E2EF] flex flex-col gap-4" data-id="element-1768">
                <button onClick={() => {
              setView('client-dashboard');
              setMobileMenuOpen(false);
            }} className="w-full px-6 py-4 border-2 border-[#00A1E0] text-[#00A1E0] rounded-2xl text-base font-black hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-1769">
                  Client Login
                </button>
                <button onClick={() => {
              setView('demo');
              setMobileMenuOpen(false);
            }} className="w-full px-6 py-4 bg-[#00A1E0] text-white rounded-2xl text-base font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-1770">
                  Demo
                </button>
                {onBack && <button onClick={onBack} className="w-full py-4 text-sm font-bold uppercase tracking-widest text-[#1B264F]/60 hover:text-[#1B264F] transition-colors" data-id="element-1771">
                    ← Back to Admin
                  </button>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {view === 'client-dashboard' && <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} transition={{
      duration: 0.4
    }} className="flex flex-col md:flex-row min-h-[calc(100vh-73px)]" data-id="element-1772">
          {/* Dashboard Sidebar */}
          <div className="w-full md:w-64 bg-[#1B264F] text-white p-4 md:p-6 flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible no-scrollbar shrink-0" data-id="element-1773">
            {[{
          id: 'dashboard',
          label: 'Dashboard',
          icon: LayoutIcon
        }, {
          id: 'domains',
          label: 'Domains',
          icon: Globe
        }, {
          id: 'dns',
          label: 'DNS Management',
          icon: Layers
        }, {
          id: 'billing',
          label: 'Billing',
          icon: CreditCard
        }, {
          id: 'email-marketing',
          label: 'E-mail Marketing',
          icon: Mail
        }, {
          id: 'crm',
          label: 'CRM',
          icon: Users
        }, {
          id: 'sms',
          label: 'SMS Blast',
          icon: MessageSquare
        }, {
          id: 'pro-email',
          label: 'Pro Email',
          icon: Shield
        }, {
          id: 'ssl',
          label: 'SSL Certificates',
          icon: Lock
        }].map(tab => <button key={tab.id} onClick={() => setActiveDashboardTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap md:whitespace-normal ${activeDashboardTab === tab.id ? 'bg-[#00A1E0] text-white shadow-lg shadow-[#00A1E0]/20' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-id="element-1774">
                <tab.icon size={18} data-id="element-1775" />
                {tab.label}
              </button>)}
          </div>

          {/* Dashboard Content */}
          <div className="flex-1 p-4 md:p-10 overflow-y-auto" data-id="element-1776">
            <div className="max-w-5xl mx-auto" data-id="element-1777">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-10" data-id="element-1778">
                <div data-id="element-1779">
                  <h2 className="text-2xl md:text-3xl font-black text-[#1B264F] uppercase tracking-tight" data-id="element-1780">
                    {activeDashboardTab.replace('-', ' ')}
                  </h2>
                  <p className="text-slate-500 font-medium text-sm md:text-base" data-id="element-1781">
                    Manage your enterprise assets and communications.
                  </p>
                </div>
                {activeDashboardTab === 'sms' && <button className="w-full md:w-auto px-6 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm shadow-lg shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-1782">
                    Add SMS Credit
                  </button>}
              </div>

              <div className="bg-white rounded-[24px] md:rounded-[32px] border border-[#D8E2EF] shadow-sm p-4 md:p-8 min-h-[500px]" data-id="element-1783">
                {activeDashboardTab === 'dashboard' && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-1784">
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-1785">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-1786">
                        Active Domains
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-1787">4</p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-1788">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-1789">
                        CRM Leads
                      </p>
                      <p className="text-2xl font-black text-[#00A1E0]" data-id="element-1790">
                        1,284
                      </p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-1791">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-1792">
                        SMS Balance
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-1793">
                        $42.50
                      </p>
                    </div>
                  </div>}

                {activeDashboardTab === 'domains' && <div className="space-y-4" data-id="element-1794">
                    {['agenttoboss.com', 'myagency.io', 'realtypro.net'].map(domain => <div key={domain} className="flex items-center justify-between p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-1795">
                          <div className="flex items-center gap-3" data-id="element-1796">
                            <Globe size={20} className="text-[#00A1E0]" data-id="element-1797" />
                            <span className="font-bold text-[#1B264F]" data-id="element-1798">
                              {domain}
                            </span>
                          </div>
                          <div className="flex gap-2" data-id="element-1799">
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-1800">
                              Manage
                            </button>
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-1801">
                              DNS
                            </button>
                          </div>
                        </div>)}
                  </div>}

                {activeDashboardTab === 'sms' && <div className="space-y-8" data-id="element-1802">
                    <div className="p-8 bg-[#1B264F] text-white rounded-2xl relative overflow-hidden" data-id="element-1803">
                      <div className="relative z-10" data-id="element-1804">
                        <p className="text-xs font-black uppercase text-white/40 mb-2" data-id="element-1805">
                          Available Credit
                        </p>
                        <p className="text-5xl font-black" data-id="element-1806">$42.50</p>
                        <p className="text-sm text-white/60 mt-4" data-id="element-1807">
                          Estimated 2,360 SMS segments remaining.
                        </p>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-1808"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1809">
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-1810">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-1811">
                          Quick Blast
                        </h4>
                        <textarea className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl p-4 text-sm focus:outline-none min-h-[100px]" placeholder="Enter your SMS message..." data-id="element-1812" />
                        <button className="w-full mt-4 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm" data-id="element-1813">
                          Send Blast
                        </button>
                      </div>
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-1814">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-1815">
                          Auto-Topup
                        </h4>
                        <div className="flex items-center justify-between p-3 bg-[#F3F6F9] rounded-xl" data-id="element-1816">
                          <span className="text-sm font-bold" data-id="element-1817">
                            Enable Auto-Topup
                          </span>
                          <div className="w-10 h-5 bg-[#00A1E0] rounded-full relative" data-id="element-1818">
                            <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-1819"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Placeholder for other tabs */}
                {['dns', 'billing', 'email-marketing', 'crm', 'pro-email', 'ssl'].includes(activeDashboardTab) && activeDashboardTab !== 'sms' && activeDashboardTab !== 'domains' && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1820">
                      <div className="w-20 h-20 bg-[#F3F6F9] rounded-3xl flex items-center justify-center text-[#00A1E0] mb-6" data-id="element-1821">
                        <Zap size={40} data-id="element-1822" />
                      </div>
                      <h3 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1823">
                        Enterprise {activeDashboardTab.replace('-', ' ')}
                      </h3>
                      <p className="text-slate-500 max-w-xs mx-auto" data-id="element-1824">
                        Your {activeDashboardTab} services are active and being
                        managed by our enterprise architects.
                      </p>
                    </div>}
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'pricing' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1825">
          <div className="max-w-7xl mx-auto" data-id="element-1826">
            <div className="text-center mb-20" data-id="element-1827">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-1828">
                Transparent Comparison
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-1829">
                See how Agenttoboss stacks up against traditional CRM and
                marketing platforms.
              </p>
            </div>

            <div className="overflow-x-auto" data-id="element-1830">
              <table className="w-full border-collapse bg-white rounded-[32px] overflow-hidden shadow-2xl border border-[#D8E2EF]" data-id="element-1831">
                <thead data-id="element-1832">
                  <tr className="bg-[#1B264F] text-white" data-id="element-1833">
                    <th className="p-8 text-left text-lg font-black" data-id="element-1834">
                      Feature
                    </th>
                    <th className="p-8 text-center text-lg font-black bg-[#00A1E0]" data-id="element-1835">
                      Agenttoboss
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-1836">
                      Traditional CRMs
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-1837">
                      Marketing Agencies
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#1B264F]" data-id="element-1838">
                  {[{
                f: 'Custom 5-Pager Website',
                a: 'Included (Free)',
                b: '$2,500+ Setup',
                c: '$5,000+ Setup'
              }, {
                f: 'CRM + 100k Email Blasts',
                a: 'Included (Free)',
                b: '$150+/mo',
                c: 'N/A'
              }, {
                f: 'Social Media (4 Channels)',
                a: '$150/mo',
                b: 'N/A',
                c: '$1,000+/mo'
              }, {
                f: 'AI Telephony (Tier 2)',
                a: '$119/seat',
                b: '$250+/seat',
                c: 'N/A'
              }, {
                f: 'Review Management',
                a: 'Included',
                b: 'Add-on ($50/mo)',
                c: 'Included'
              }, {
                f: 'Business Consulting',
                a: 'Included (Advance)',
                b: 'N/A',
                c: '$200+/hr'
              }].map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F3F6F9]'} data-id="element-1839">
                      <td className="p-8 font-bold border-b border-[#D8E2EF]" data-id="element-1840">
                        {row.f}
                      </td>
                      <td className="p-8 text-center font-black text-[#00A1E0] border-b border-[#D8E2EF] bg-[#00A1E0]/5" data-id="element-1841">
                        {row.a}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-1842">
                        {row.b}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-1843">
                        {row.c}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-20 bg-[#1B264F] rounded-[40px] p-12 text-white text-center" data-id="element-1844">
              <h2 className="text-3xl font-black mb-6" data-id="element-1845">
                Ready to switch and save?
              </h2>
              <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all" data-id="element-1846">
                Get a Custom Quote
              </button>
            </div>
          </div>
        </motion.div>}

      {/* Informational Pages */}
      {['crm-core', 'automations', 'analytics', 'integrations', 'about', 'careers', 'press', 'contact', 'privacy', 'terms', 'security'].includes(view) && <motion.div initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.02
    }} transition={{
      duration: 0.4
    }} className="py-24 px-8 min-h-[60vh]" data-id="element-1847">
          <div className="max-w-4xl mx-auto" data-id="element-1848">
            <div className="mb-12" data-id="element-1849">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6 capitalize tracking-tight" data-id="element-1850">
                {view.replace('-', ' ')}
              </h1>
              <div className="h-1.5 w-24 bg-[#00A1E0] rounded-full mb-8" data-id="element-1851"></div>
            </div>

            <div className="prose prose-slate prose-lg max-w-none text-[#1B264F]/70 font-medium leading-relaxed space-y-8" data-id="element-1852">
              {view === 'crm-core' && <>
                  <p data-id="element-1853">
                    Our CRM Core is the heartbeat of your agency. Built for
                    high-performance teams, it centralizes every interaction,
                    lead, and deal into a single, lightning-fast interface.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 not-prose" data-id="element-1854">
                    {[{
                title: 'Unified Inbox',
                desc: 'Every email, SMS, and call in one place.'
              }, {
                title: 'Smart Pipelines',
                desc: 'Visualize your deals with drag-and-drop ease.'
              }, {
                title: 'Lead Enrichment',
                desc: 'Automatic profile building for every new lead.'
              }, {
                title: 'Activity Tracking',
                desc: 'Never lose track of a follow-up again.'
              }].map((item, i) => <div key={i} className="p-6 bg-white rounded-2xl border border-[#D8E2EF] shadow-sm" data-id="element-1855">
                        <h4 className="font-black text-[#1B264F] mb-2" data-id="element-1856">
                          {item.title}
                        </h4>
                        <p className="text-sm" data-id="element-1857">{item.desc}</p>
                      </div>)}
                  </div>
                </>}

              {view === 'automations' && <>
                  <p data-id="element-1858">
                    Stop doing manual work. Our agentic automation engine
                    handles the repetitive tasks so you can focus on closing
                    deals.
                  </p>
                  <ul className="space-y-4 list-none pl-0" data-id="element-1859">
                    {['Automated lead distribution based on performance', 'Instant SMS/Email auto-responders', 'Trigger-based workflow sequences', 'AI-driven follow-up reminders'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-1860">
                        <div className="w-6 h-6 bg-[#00A1E0]/10 rounded-full flex items-center justify-center text-[#00A1E0]" data-id="element-1861">
                          <Check size={14} data-id="element-1862" />
                        </div>
                        {item}
                      </li>)}
                  </ul>
                </>}

              {view === 'analytics' && <>
                  <p data-id="element-1863">
                    Data-driven decisions start here. Get deep insights into
                    your funnel performance, agent productivity, and marketing
                    ROI.
                  </p>
                  <div className="h-64 bg-[#1B264F] rounded-3xl p-8 flex items-end gap-2" data-id="element-1864">
                    {[60, 40, 80, 50, 90, 70, 85].map((h, i) => <div key={i} className="flex-1 bg-[#00A1E0]/20 rounded-t-lg" style={{
                height: `${h}%`
              }} data-id="element-1865">
                        <div className="w-full bg-[#00A1E0] rounded-t-lg" style={{
                  height: '30%'
                }} data-id="element-1866"></div>
                      </div>)}
                  </div>
                </>}

              {view === 'integrations' && <>
                  <p data-id="element-1867">
                    AgentToBoss plays well with others. Connect your favorite
                    tools and create a seamless tech stack for your agency.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 not-prose" data-id="element-1868">
                    {['Salesforce', 'Zillow', 'Slack', 'Gmail', 'Twilio', 'Stripe', 'Zapier', 'HubSpot'].map(tool => <div key={tool} className="p-4 bg-white rounded-xl border border-[#D8E2EF] text-center font-bold text-sm" data-id="element-1869">
                        {tool}
                      </div>)}
                  </div>
                </>}

              {view === 'about' && <>
                  <p data-id="element-1870">
                    Founded in 2024, AgentToBoss was built by agents, for
                    agents. We saw the limitations of traditional CRMs and
                    decided to build the command center we always wanted.
                  </p>
                  <p data-id="element-1871">
                    Our mission is to empower the top 1% of sales organizations
                    with the infrastructure they need to dominate their markets.
                  </p>
                </>}

              {view === 'careers' && <>
                  <p data-id="element-1872">
                    We're looking for the best engineers, designers, and scaling
                    specialists to help us build the future of sales technology.
                  </p>
                  <div className="space-y-4 not-prose" data-id="element-1873">
                    {['Senior Frontend Engineer', 'Product Designer', 'Customer Success Architect'].map(job => <div key={job} className="p-6 bg-white rounded-2xl border border-[#D8E2EF] flex justify-between items-center" data-id="element-1874">
                        <span className="font-bold" data-id="element-1875">{job}</span>
                        <button className="text-[#00A1E0] font-black text-sm" data-id="element-1876">
                          Apply Now →
                        </button>
                      </div>)}
                  </div>
                </>}

              {view === 'contact' && <div className="not-prose" data-id="element-1877">
                  <p className="mb-8 font-medium text-lg" data-id="element-1878">
                    Need help? Our team is available 24/7 for enterprise
                    partners.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1879">
                    <div className="space-y-4" data-id="element-1880">
                      <div className="p-6 bg-white rounded-2xl border border-[#D8E2EF]" data-id="element-1881">
                        <Mail className="text-[#00A1E0] mb-4" data-id="element-1882" />
                        <h4 className="font-black" data-id="element-1883">Email Us</h4>
                        <p className="text-sm" data-id="element-1884">support@agenttoboss.com</p>
                      </div>
                      <div className="p-6 bg-white rounded-2xl border border-[#D8E2EF]" data-id="element-1885">
                        <MessageSquare className="text-[#00A1E0] mb-4" data-id="element-1886" />
                        <h4 className="font-black" data-id="element-1887">Live Chat</h4>
                        <p className="text-sm" data-id="element-1888">Available in your dashboard</p>
                      </div>
                    </div>
                    <form className="space-y-4" data-id="element-1889">
                      <input type="text" placeholder="Name" className="w-full p-4 bg-white border border-[#D8E2EF] rounded-xl focus:outline-none focus:border-[#00A1E0]" data-id="element-1890" />
                      <textarea placeholder="Message" rows={4} className="w-full p-4 bg-white border border-[#D8E2EF] rounded-xl focus:outline-none focus:border-[#00A1E0]" data-id="element-1891" />
                      <button className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black" data-id="element-1892">
                        Send Message
                      </button>
                    </form>
                  </div>
                </div>}

              {['privacy', 'terms', 'security'].includes(view) && <div className="space-y-6" data-id="element-1893">
                  <p data-id="element-1894">Last updated: May 19, 2026</p>
                  <h3 className="text-2xl font-black text-[#1B264F]" data-id="element-1895">
                    1. Overview
                  </h3>
                  <p data-id="element-1896">
                    This document outlines our commitment to your{' '}
                    {view === 'security' ? 'data protection' : 'rights and responsibilities'}{' '}
                    when using the AgentToBoss platform.
                  </p>
                  <h3 className="text-2xl font-black text-[#1B264F]" data-id="element-1897">
                    2. Data Handling
                  </h3>
                  <p data-id="element-1898">
                    We use enterprise-grade encryption and follow strict
                    industry standards to ensure your business data remains
                    private and secure at all times.
                  </p>
                </div>}
            </div>

            <button onClick={() => setView('home')} className="mt-16 inline-flex items-center gap-2 text-[#00A1E0] font-black hover:gap-3 transition-all" data-id="element-1899">
              ← Back to Home
            </button>
          </div>
        </motion.div>}

      {view === 'enterprise' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="overflow-hidden" data-id="element-1900">
          {/* Enterprise Hero */}
          <section className="relative py-32 px-8 bg-[#1B264F] text-white" data-id="element-1901">
            <div className="absolute inset-0 opacity-20" data-id="element-1902">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000" alt="Enterprise Background" className="w-full h-full object-cover" data-id="element-1903" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/80 to-transparent" data-id="element-1904"></div>
            </div>

            <div className="w-full mx-auto relative z-10 px-8" data-id="element-1905">
              <div className="max-w-3xl" data-id="element-1906">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-black uppercase tracking-widest mb-8" data-id="element-1907">
                  Enterprise Solutions
                </div>
                <h1 className="text-6xl lg:text-8xl font-black mb-8 tracking-tighter leading-[0.9]" data-id="element-1908">
                  Built for the <br data-id="element-1909" />
                  <span className="text-[#00A1E0]" data-id="element-1910">1% of Agencies.</span>
                </h1>
                <p className="text-xl text-white/60 font-medium leading-relaxed mb-10" data-id="element-1911">
                  Scale without limits. Our enterprise tier provides the
                  infrastructure, security, and custom engineering required by
                  the world's largest real estate and sales organizations.
                </p>
                <div className="flex gap-4" data-id="element-1912">
                  <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30" data-id="element-1913">
                    Speak with an Architect
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Custom Integrations Section */}
          <section className="py-32 px-8 bg-white" data-id="element-1914">
            <div className="max-w-7xl mx-auto" data-id="element-1915">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-1916">
                <div data-id="element-1917">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-8 tracking-tight" data-id="element-1918">
                    Custom Engineering & <br data-id="element-1919" />
                    <span className="text-[#00A1E0]" data-id="element-1920">Deep Integrations</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-12" data-id="element-1921">
                    Off-the-shelf software doesn't cut it at your scale. Our
                    dedicated engineering team builds custom bridges between
                    Agenttoboss and your existing tech stack.
                  </p>

                  <div className="space-y-8" data-id="element-1922">
                    <div className="flex gap-6" data-id="element-1923">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1924">
                        <Zap size={28} data-id="element-1925" />
                      </div>
                      <div data-id="element-1926">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1927">
                          Enterprise Telephony
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1928">
                          Custom SIP trunking, advanced IVR flows, and global
                          carrier integrations tailored to your specific call
                          center requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-1929">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1930">
                        <Layers size={28} data-id="element-1931" />
                      </div>
                      <div data-id="element-1932">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1933">
                          Proprietary AI Workflows
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1934">
                          We deploy custom LLM agents trained on your unique
                          business data to automate lead qualification and
                          complex back-office tasks.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-1935">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1936">
                        <Shield size={28} data-id="element-1937" />
                      </div>
                      <div data-id="element-1938">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1939">
                          SSO & Governance
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1940">
                          Enterprise-grade security with SAML/SSO, custom RBAC,
                          and comprehensive audit logging for compliance-heavy
                          environments.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-1941">
                  <div className="bg-[#1B264F] rounded-[40px] p-12 shadow-2xl relative z-10" data-id="element-1942">
                    <div className="space-y-6" data-id="element-1943">
                      <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl" data-id="element-1944">
                        <span className="text-white/60 font-bold" data-id="element-1945">
                          API Status
                        </span>
                        <span className="text-[#00A1E0] font-black" data-id="element-1946">
                          99.99% Uptime
                        </span>
                      </div>
                      <div className="h-48 bg-[#F3F6F9]/5 rounded-2xl border border-white/10 p-6" data-id="element-1947">
                        <div className="flex gap-2 mb-4" data-id="element-1948">
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]" data-id="element-1949"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-1950"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-1951"></div>
                        </div>
                        <div className="space-y-2" data-id="element-1952">
                          <div className="h-2 w-3/4 bg-white/10 rounded" data-id="element-1953"></div>
                          <div className="h-2 w-1/2 bg-white/10 rounded" data-id="element-1954"></div>
                          <div className="h-2 w-2/3 bg-white/10 rounded" data-id="element-1955"></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1956">
                        <div className="p-4 bg-[#00A1E0] rounded-2xl text-center" data-id="element-1957">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-1958">
                            Endpoints
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-1959">450+</p>
                        </div>
                        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl text-center" data-id="element-1960">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-1961">
                            Latency
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-1962">12ms</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Decorative Glow */}
                  <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full" data-id="element-1963"></div>
                </div>
              </div>
            </div>
          </section>

          {/* Enterprise CTA Form */}
          <section className="py-32 px-8 bg-[#F3F6F9]" data-id="element-1964">
            <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl p-12" data-id="element-1965">
              <div className="text-center mb-12" data-id="element-1966">
                <h2 className="text-4xl font-black text-[#1B264F] mb-4" data-id="element-1967">
                  Request Enterprise Access
                </h2>
                <p className="text-[#1B264F]/60 font-medium" data-id="element-1968">
                  Our enterprise architects will reach out within 2 hours.
                </p>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1969">
                <div className="space-y-2" data-id="element-1970">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1971">
                    Company Name
                  </label>
                  <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Global Realty Group" data-id="element-1972" />
                </div>
                <div className="space-y-2" data-id="element-1973">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1974">
                    Estimated Seats
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-1975">
                    <option data-id="element-1976">50-100 seats</option>
                    <option data-id="element-1977">100-500 seats</option>
                    <option data-id="element-1978">500+ seats</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1979">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1980">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="vp@globalrealty.com" data-id="element-1981" />
                </div>
                <div className="space-y-2" data-id="element-1982">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1983">
                    Request Type
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-1984">
                    <option data-id="element-1985">Book a Demo</option>
                    <option data-id="element-1986">Request Callback</option>
                    <option data-id="element-1987">Custom Integration Quote</option>
                  </select>
                </div>
                <button className="md:col-span-2 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-1988">
                  Submit Enterprise Request
                </button>
              </form>
            </div>
          </section>
        </motion.div>}

      {view === 'platform' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1989">
          <div className="max-w-7xl mx-auto" data-id="element-1990">
            <div className="mb-16" data-id="element-1991">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-1992">
                The Scaling Engine
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-3xl font-medium" data-id="element-1993">
                Our platform is designed to eliminate the technical overhead of
                running a high-performance agency. Here is how we enable you to
                scale.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1994">
              <div className="space-y-8" data-id="element-1995">
                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-1996">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1997">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-1998">
                      <LayoutIcon size={24} data-id="element-1999" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-2000">Websites & Hosting</h3>
                  </div>
                  <div className="space-y-6" data-id="element-2001">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-2002">
                      <div data-id="element-2003">
                        <p className="font-bold text-[#1B264F]" data-id="element-2004">
                          5-Pager Standard Website
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2005">
                          Free per our standards
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2006">
                        $20/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-2007">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-2008">
                      <div data-id="element-2009">
                        <p className="font-bold text-[#1B264F]" data-id="element-2010">
                          Enterprise (10+ Pages)
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2011">
                          Custom high-capacity architecture
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2012">
                        $65/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-2013">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-2014">
                      <div data-id="element-2015">
                        <p className="font-bold text-[#1B264F]" data-id="element-2016">
                          E-commerce Solution
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2017">
                          Full store integration
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2018">
                        $70/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-2019">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-2020">
                  <div className="flex items-center gap-4 mb-6" data-id="element-2021">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-2022">
                      <Target size={24} data-id="element-2023" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-2024">Marketing & CRM</h3>
                  </div>
                  <div className="space-y-6" data-id="element-2025">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-2026">
                      <div data-id="element-2027">
                        <p className="font-bold text-[#1B264F]" data-id="element-2028">
                          Social Media Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2029">
                          4 Accounts + GMB/Yelp Setup + Review Mgmt
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2030">$150/mo</p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-2031">
                      <div data-id="element-2032">
                        <p className="font-bold text-[#1B264F]" data-id="element-2033">
                          CRM & Email Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2034">
                          First 100,000 email blasts per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2035">FREE</p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-2036">
                      <div data-id="element-2037">
                        <p className="font-bold text-[#1B264F]" data-id="element-2038">
                          High Volume Email
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2039">
                          Up to 1M email sends per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-2040">+$15/mo</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8" data-id="element-2041">
                <div className="bg-[#1B264F] text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden" data-id="element-2042">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-2043"></div>
                  <div className="flex items-center gap-4 mb-8 relative z-10" data-id="element-2044">
                    <div className="w-12 h-12 bg-[#00A1E0] rounded-2xl flex items-center justify-center text-white" data-id="element-2045">
                      <Zap size={24} fill="currentColor" data-id="element-2046" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-2047">Phone Systems</h3>
                  </div>
                  <div className="space-y-8 relative z-10" data-id="element-2048">
                    <div className="p-6 bg-white/5 border border-white/10 rounded-2xl" data-id="element-2049">
                      <div className="flex justify-between items-center mb-4" data-id="element-2050">
                        <h4 className="font-black text-[#00A1E0]" data-id="element-2051">
                          Tier 1: Basic
                        </h4>
                        <p className="text-2xl font-black" data-id="element-2052">
                          $35
                          <span className="text-xs text-white/40" data-id="element-2053">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/60 mb-4" data-id="element-2054">
                        Voice + CRM Integration. Perfect for small teams.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/40" data-id="element-2055">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="p-6 bg-[#00A1E0] rounded-2xl shadow-xl" data-id="element-2056">
                      <div className="flex justify-between items-center mb-4" data-id="element-2057">
                        <h4 className="font-black text-white" data-id="element-2058">
                          Tier 2: AI Advanced
                        </h4>
                        <p className="text-2xl font-black" data-id="element-2059">
                          $119
                          <span className="text-xs text-white/70" data-id="element-2060">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/90 mb-4" data-id="element-2061">
                        Unlimited Voice + AI + Automation + Dedicated Support
                        Engineer.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70" data-id="element-2062">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10" data-id="element-2063">
                      <div className="flex justify-between text-xs font-bold" data-id="element-2064">
                        <span className="text-white/60" data-id="element-2065">SMS Segment</span>
                        <span data-id="element-2066">$0.018</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold mt-2" data-id="element-2067">
                        <span className="text-white/60" data-id="element-2068">MMS Segment</span>
                        <span data-id="element-2069">$0.050</span>
                      </div>
                      <p className="text-[10px] text-white/40 mt-4 italic text-center" data-id="element-2070">
                        *Min $20 wallet required for SMS enablement
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-2071">
                  <h3 className="text-xl font-black mb-6" data-id="element-2072">
                    Add-ons & Essentials
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2073">
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-2074">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-2075">
                        SSL Certificate
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-2076">
                        $30/year
                      </p>
                    </div>
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-2077">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-2078">
                        Professional Email
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-2079">
                        5 FREE
                      </p>
                      <p className="text-[10px] text-[#1B264F]/40 font-bold" data-id="element-2080">
                        $3/extra
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'demo' && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.05
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-2081">
          <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl overflow-hidden flex flex-col md:flex-row" data-id="element-2082">
            <div className="md:w-1/2 bg-[#1B264F] p-12 text-white flex flex-col justify-between" data-id="element-2083">
              <div data-id="element-2084">
                <h2 className="text-4xl font-black mb-6" data-id="element-2085">
                  Experience the Power.
                </h2>
                <p className="text-white/60 font-medium leading-relaxed mb-8" data-id="element-2086">
                  Schedule a 15-minute walkthrough of the Agenttoboss platform
                  and see how we can automate your growth.
                </p>
                <div className="space-y-6" data-id="element-2087">
                  <div className="flex items-center gap-4" data-id="element-2088">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-2089">
                      <Check size={20} data-id="element-2090" />
                    </div>
                    <span className="font-bold" data-id="element-2091">Live CRM Walkthrough</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-2092">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-2093">
                      <Check size={20} data-id="element-2094" />
                    </div>
                    <span className="font-bold" data-id="element-2095">Automation Strategy</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-2096">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-2097">
                      <Check size={20} data-id="element-2098" />
                    </div>
                    <span className="font-bold" data-id="element-2099">Custom Pricing Quote</span>
                  </div>
                </div>
              </div>
              <div className="pt-12 border-t border-white/10" data-id="element-2100">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-2101">
                  Trusted by 10,000+ Agents
                </p>
              </div>
            </div>
            <div className="md:w-1/2 p-12" data-id="element-2102">
              <h3 className="text-2xl font-black mb-8" data-id="element-2103">Schedule Your Demo</h3>
              <form className="space-y-6" data-id="element-2104">
                <div className="grid grid-cols-2 gap-4" data-id="element-2105">
                  <div className="space-y-2" data-id="element-2106">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2107">
                      First Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="John" data-id="element-2108" />
                  </div>
                  <div className="space-y-2" data-id="element-2109">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2110">
                      Last Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="Doe" data-id="element-2111" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-2112">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2113">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="john@agency.com" data-id="element-2114" />
                </div>
                <div className="space-y-2" data-id="element-2115">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2116">
                    Select Date
                  </label>
                  <div className="grid grid-cols-3 gap-2" data-id="element-2117">
                    {['May 15', 'May 16', 'May 17'].map(d => <button key={d} type="button" className="py-2 bg-[#F3F6F9] border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-2118">
                        {d}
                      </button>)}
                  </div>
                </div>
                <button type="submit" className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-2119">
                  Confirm Meeting
                </button>
              </form>
            </div>
          </div>
        </motion.div>}

      {view === 'domains' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-2120">
          <div className="max-w-7xl mx-auto" data-id="element-2121">
            <div className="text-center mb-16" data-id="element-2122">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-2123">
                Find Your Digital Home
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-2124">
                Secure your domain and choose a hosting plan built for
                enterprise performance.
              </p>
            </div>

            {/* Domain Search */}
            <div className="max-w-3xl mx-auto mb-24" data-id="element-2125">
              <div className="bg-white p-2 rounded-2xl shadow-2xl border border-[#D8E2EF] flex items-center gap-2" data-id="element-2126">
                <div className="pl-6 text-[#1B264F]/30" data-id="element-2127">
                  <LayoutIcon size={24} data-id="element-2128" />
                </div>
                <input type="text" placeholder="search-your-dream-domain.com" className="flex-1 bg-transparent px-4 py-5 text-lg font-bold text-[#1B264F] focus:outline-none" data-id="element-2129" />
                <button className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-2130">
                  Search
                </button>
              </div>
              <div className="mt-6 flex justify-center gap-8 text-sm font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-2131">
                <span data-id="element-2132">.com $12.99</span>
                <span data-id="element-2133">.net $14.99</span>
                <span data-id="element-2134">.org $15.99</span>
                <span data-id="element-2135">.ai $59.99</span>
              </div>
            </div>

            {/* Hosting Packages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2136">
              <div className="p-10 bg-white border border-[#D8E2EF] rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-2137">
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-2138">
                  Starter
                </h3>
                <div className="mb-8" data-id="element-2139">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-2140">
                    $29
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-2141">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-2142">
                  {['5 Pager Website', 'CRM Integration', 'Standard SSL', '24/7 Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-2143">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-2144" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#F3F6F9] text-[#1B264F] rounded-xl font-black hover:bg-[#D8E2EF] transition-all" data-id="element-2145">
                  Select Starter
                </button>
              </div>

              <div className="p-10 bg-white border-2 border-[#00A1E0] rounded-3xl shadow-2xl relative scale-105" data-id="element-2146">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00A1E0] text-white text-[10px] font-black uppercase tracking-widest rounded-full" data-id="element-2147">
                  Recommended
                </div>
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-2148">Pro</h3>
                <div className="mb-8" data-id="element-2149">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-2150">
                    $79
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-2151">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-2152">
                  {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-2153">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-2154" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-2155">
                  Select Pro
                </button>
              </div>

              <div className="p-10 bg-[#1B264F] text-white rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-2156">
                <h3 className="text-2xl font-black mb-2" data-id="element-2157">Advance</h3>
                <div className="mb-8" data-id="element-2158">
                  <span className="text-4xl font-black" data-id="element-2159">$199</span>
                  <span className="text-white/40 font-bold ml-2" data-id="element-2160">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-2161">
                  {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-white/80" data-id="element-2162">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-2163" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-black transition-all" data-id="element-2164">
                  Select Advance
                </button>
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'home' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-2165">
          {/* Hero Section */}
          <section className="relative pt-10 pb-20 px-8 overflow-hidden min-h-[80vh] flex items-center" data-id="element-2166">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0" data-id="element-2167">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000" alt="Real Estate Agent" className="w-full h-full object-cover" data-id="element-2168" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/90 to-[#1B264F]/40" data-id="element-2169"></div>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-16 items-center relative z-10" data-id="element-2170">
              <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2171">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-2172">
                  <span className="relative flex h-2 w-2" data-id="element-2173">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00A1E0] opacity-75" data-id="element-2174"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00A1E0]" data-id="element-2175"></span>
                  </span>
                  Next-Gen CRM for High-Performers
                </div>
                <h1 className="text-4xl lg:text-7xl font-black text-white leading-[0.9] mb-8 tracking-tighter" data-id="element-2176">
                  Organize.
                  <br data-id="element-2177" />
                  Scale.
                  <br data-id="element-2178" />
                  <span className="text-[#00A1E0]" data-id="element-2179">Dominate.</span>
                </h1>
                <p className="text-xl text-white/70 max-w-xl mb-10 leading-relaxed font-medium" data-id="element-2180">
                  The enterprise-grade command center for agents who refuse to
                  settle. Automate your workflow, track every lead, and crush
                  your quotas with Agenttoboss.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-2181">
                  <button onClick={() => setView('demo')} className="w-full sm:w-auto px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-bold text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30 flex items-center justify-center gap-2 group" data-id="element-2182">
                    Demo{' '}
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-2183" />
                  </button>
                  <button onClick={() => setVideoModalOpen(true)} className="w-full sm:w-auto px-10 py-5 bg-white text-[#1B264F] border border-[#D8E2EF] rounded-xl font-bold text-lg hover:bg-[#F3F6F9] transition-all" data-id="element-2184">
                    Watch Video
                  </button>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            rotateY: -10
          }} whileInView={{
            opacity: 1,
            scale: 1,
            rotateY: 0
          }} viewport={{
            once: true,
            delay: 0.2
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative perspective-1000" data-id="element-2185">
                <div className="bg-white rounded-2xl shadow-[0_32px_64px_-12px_rgba(27,38,79,0.2)] border border-[#D8E2EF] p-6 overflow-hidden" data-id="element-2186">
                  <div className="flex items-center justify-between mb-8" data-id="element-2187">
                    <div className="flex gap-1.5" data-id="element-2188">
                      <div className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-2189"></div>
                      <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-2190"></div>
                      <div className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-2191"></div>
                    </div>
                    <div className="px-3 py-1 bg-[#F3F6F9] rounded-md text-[10px] font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-2192">
                      Lead Funnel v2.4
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-2193">
                    <div data-id="element-2194">
                      <div className="flex justify-between mb-2" data-id="element-2195">
                        <span className="text-xs font-bold text-[#1B264F]" data-id="element-2196">
                          Active Lead Funnel
                        </span>
                        <span className="text-xs font-bold text-[#00A1E0]" data-id="element-2197">
                          84% Efficiency
                        </span>
                      </div>
                      <div className="h-24 flex items-end gap-1" data-id="element-2198">
                        {[40, 70, 45, 90, 65, 80, 55, 95, 75, 85].map((h, i) => <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height: `${h}%`
                    }} transition={{
                      delay: 0.5 + i * 0.05
                    }} className={`flex-1 rounded-t-sm ${i === 7 ? 'bg-[#00A1E0]' : 'bg-[#00A1E0]/20'}`} data-id="element-2199" />)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-2200">
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-2201">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-2202">
                          New Leads
                        </p>
                        <p className="text-2xl font-black text-[#1B264F]" data-id="element-2203">
                          1,284
                        </p>
                      </div>
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-2204">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-2205">
                          Conversion
                        </p>
                        <p className="text-2xl font-black text-[#00A1E0]" data-id="element-2206">
                          +12.4%
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-2207">
                      <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase" data-id="element-2208">
                        Active Reminders
                      </p>
                      {[{
                    title: 'Follow up with Sarah J.',
                    time: 'In 12 mins',
                    icon: Users
                  }, {
                    title: 'Quarterly Review - Acme Corp',
                    time: 'In 2 hours',
                    icon: BarChart3
                  }].map((rem, i) => <div key={i} className="flex items-center justify-between p-3 bg-white border border-[#D8E2EF] rounded-lg shadow-sm" data-id="element-2209">
                          <div className="flex items-center gap-3" data-id="element-2210">
                            <div className="w-8 h-8 bg-[#00A1E0]/10 rounded flex items-center justify-center text-[#00A1E0]" data-id="element-2211">
                              <rem.icon size={16} data-id="element-2212" />
                            </div>
                            <span className="text-xs font-bold" data-id="element-2213">
                              {rem.title}
                            </span>
                          </div>
                          <span className="text-[10px] font-bold text-[#00A1E0]" data-id="element-2214">
                            {rem.time}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Decorative Glow */}
                <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#00A1E0]/5 blur-[100px] rounded-full" data-id="element-2215"></div>
              </motion.div>
            </div>
          </section>

          {/* Process Grid */}
          <section className="py-24 px-8 bg-white border-y border-[#D8E2EF]" data-id="element-2216">
            <div className="max-w-7xl mx-auto" data-id="element-2217">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2218">
                {[{
              title: 'Automated Sourcing',
              desc: 'AI-driven lead discovery across 50+ platforms.',
              icon: Target,
              image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Smart Enrichment',
              desc: 'Instant data validation and profile building.',
              icon: Layers,
              image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Agentic Outreach',
              desc: 'Personalized sequences that sound human.',
              icon: MousePointer2,
              image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Real-time Analytics',
              desc: 'Deep insights into every stage of your funnel.',
              icon: BarChart3,
              image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800'
            }].map((card, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} whileHover={{
              y: -8
            }} className="bg-white border border-[#1B264F]/10 rounded-[12px] hover:border-[#00A1E0] hover:shadow-xl hover:shadow-[#00A1E0]/5 transition-all group overflow-hidden" data-id="element-2219">
                    <div className="aspect-video w-full overflow-hidden" data-id="element-2220">
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2221" />
                    </div>
                    <div className="p-8" data-id="element-2222">
                      <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-lg flex items-center justify-center text-[#00A1E0] mb-6 group-hover:bg-[#00A1E0] group-hover:text-white transition-colors" data-id="element-2223">
                        <card.icon size={24} data-id="element-2224" />
                      </div>
                      <h3 className="text-lg font-black text-[#1B264F] mb-3" data-id="element-2225">
                        {card.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 leading-relaxed font-medium" data-id="element-2226">
                        {card.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </section>

          {/* CRM Feature Bento - Progressive Disclosure */}
          <motion.section initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="py-32 px-8" data-id="element-2227">
            <div className="max-w-7xl mx-auto" data-id="element-2228">
              <div className="text-center mb-20" data-id="element-2229">
                <h2 className="text-4xl md:text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-2230">
                  The CRM Feature Bento
                </h2>
                <p className="text-lg text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-2231">
                  Everything you need to manage your empire, built with the
                  precision of the Salesforce Lightning system.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[350px,1fr] gap-12 items-start" data-id="element-2232">
                <div className="space-y-4" data-id="element-2233">
                  {features.map(f => <button key={f.id} onClick={() => setActiveFeature(f.id)} className={`w-full text-left p-6 rounded-2xl border transition-all ${activeFeature === f.id ? 'bg-white border-[#00A1E0] shadow-xl shadow-[#00A1E0]/5 ring-1 ring-[#00A1E0]' : 'bg-transparent border-transparent hover:bg-white/50 hover:border-[#D8E2EF]'}`} data-id="element-2234">
                      <h3 className={`text-lg font-black mb-2 ${activeFeature === f.id ? 'text-[#00A1E0]' : 'text-[#1B264F]'}`} data-id="element-2235">
                        {f.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-2236">
                        {f.desc.split('.')[0]}.
                      </p>
                    </button>)}
                </div>

                <div className="relative bg-white rounded-3xl border border-[#D8E2EF] shadow-2xl overflow-hidden aspect-[16/10]" data-id="element-2237">
                  <AnimatePresence mode="wait" data-id="element-2238">
                    <motion.div key={activeFeature} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.4
                }} className="absolute inset-0 p-8" data-id="element-2239">
                      <div className="h-full w-full rounded-xl overflow-hidden relative group" data-id="element-2240">
                        <img src={features.find(f => f.id === activeFeature)?.image} alt={activeFeature} className="w-full h-full object-cover" data-id="element-2241" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1B264F]/80 to-transparent flex items-end p-12" data-id="element-2242">
                          <div className="max-w-xl" data-id="element-2243">
                            <h4 className="text-3xl font-black text-white mb-4" data-id="element-2244">
                              {features.find(f => f.id === activeFeature)?.title}
                            </h4>
                            <p className="text-white/80 font-medium leading-relaxed" data-id="element-2245">
                              {features.find(f => f.id === activeFeature)?.desc}
                            </p>
                          </div>
                        </div>

                        {/* Agentic Glow Effect */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none" data-id="element-2246">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000" data-id="element-2247"></div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Pricing Section */}
          <section className="py-32 px-8 bg-[#1B264F] text-white overflow-hidden relative" data-id="element-2248">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10" data-id="element-2249">
              <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-[#00A1E0] blur-[150px] rounded-full" data-id="element-2250"></div>
              <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00A1E0] blur-[120px] rounded-full" data-id="element-2251"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10" data-id="element-2252">
              <div className="text-center mb-20" data-id="element-2253">
                <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight" data-id="element-2254">
                  Simple, Enterprise Pricing
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto font-medium" data-id="element-2255">
                  Choose the plan that fits your scale. No hidden fees, just
                  pure performance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-id="element-2256">
                {/* Starter Plan */}
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-2257">
                  <h3 className="text-xl font-black mb-2" data-id="element-2258">Starter</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-2259">
                    For solo agents getting started.
                  </p>
                  <div className="mb-10" data-id="element-2260">
                    <span className="text-5xl font-black" data-id="element-2261">$99</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-2262">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-2263">
                    {['5 Pager Website', 'CRM Integration', 'Standard SSL', 'Basic Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-2264">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-2265" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-2266">
                    Demo
                  </button>
                </motion.div>

                {/* Pro Plan - Highlighted */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="p-1 bg-[#00A1E0] rounded-[32px] shadow-2xl shadow-[#00A1E0]/20 transform lg:scale-110 relative" data-id="element-2267">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1.5 bg-white text-[#00A1E0] text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-xl" data-id="element-2268">
                    Most Popular
                  </div>
                  <div className="p-10 bg-[#1B264F] rounded-[28px]" data-id="element-2269">
                    <h3 className="text-2xl font-black mb-2 text-[#00A1E0]" data-id="element-2270">
                      Pro
                    </h3>
                    <p className="text-white/60 text-sm font-medium mb-8" data-id="element-2271">
                      For high-performers and small teams.
                    </p>
                    <div className="mb-10" data-id="element-2272">
                      <span className="text-6xl font-black" data-id="element-2273">$249</span>
                      <span className="text-white/40 font-bold ml-2" data-id="element-2274">/mo</span>
                    </div>
                    <ul className="space-y-4 mb-10" data-id="element-2275">
                      {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority 24/7 Support', 'Advanced Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold" data-id="element-2276">
                          <Check size={18} className="text-[#00A1E0]" data-id="element-2277" /> {f}
                        </li>)}
                    </ul>
                    <button onClick={() => setView('demo')} className="w-full py-5 bg-[#00A1E0] hover:bg-[#0089C4] text-white rounded-xl font-black text-lg transition-all shadow-xl shadow-[#00A1E0]/20" data-id="element-2278">
                      Go Pro Mode
                    </button>
                  </div>
                </motion.div>

                {/* Advance Plan */}
                <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-2279">
                  <h3 className="text-xl font-black mb-2" data-id="element-2280">Advance</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-2281">
                    For large agencies and brokerage.
                  </p>
                  <div className="mb-10" data-id="element-2282">
                    <span className="text-5xl font-black" data-id="element-2283">$899</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-2284">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-2285">
                    {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-2286">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-2287" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-2288">
                    Demo
                  </button>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Contact / Callback Section */}
          <motion.section initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="py-16 px-8 bg-white" data-id="element-2289">
            <div className="max-w-7xl mx-auto" data-id="element-2290">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-2291">
                <div data-id="element-2292">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-2293">
                    Request a <span className="text-[#00A1E0]" data-id="element-2294">Callback</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-8" data-id="element-2295">
                    Have specific questions about our platform or need help
                    choosing the right plan? Our team of experts is ready to
                    help you scale.
                  </p>
                  <div className="space-y-6" data-id="element-2296">
                    <div className="flex items-center gap-4" data-id="element-2297">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-2298">
                        <Zap size={24} data-id="element-2299" />
                      </div>
                      <div data-id="element-2300">
                        <p className="font-black text-[#1B264F]" data-id="element-2301">
                          Instant Response
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2302">
                          Average callback time: 15 minutes
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-2303">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-2304">
                        <Shield size={24} data-id="element-2305" />
                      </div>
                      <div data-id="element-2306">
                        <p className="font-black text-[#1B264F]" data-id="element-2307">
                          Expert Guidance
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-2308">
                          Speak directly with a scaling specialist
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F3F6F9] p-10 rounded-[40px] border border-[#D8E2EF] shadow-inner" data-id="element-2309">
                  <form className="space-y-6" data-id="element-2310">
                    <div className="space-y-2" data-id="element-2311">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2312">
                        Full Name
                      </label>
                      <input type="text" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Jane Smith" data-id="element-2313" />
                    </div>
                    <div className="space-y-2" data-id="element-2314">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2315">
                        Phone Number
                      </label>
                      <input type="tel" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="+1 (555) 000-0000" data-id="element-2316" />
                    </div>
                    <div className="space-y-2" data-id="element-2317">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-2318">
                        Best Time to Call
                      </label>
                      <select className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-2319">
                        <option data-id="element-2320">As soon as possible</option>
                        <option data-id="element-2321">Morning (9AM - 12PM)</option>
                        <option data-id="element-2322">Afternoon (12PM - 5PM)</option>
                        <option data-id="element-2323">Evening (5PM - 8PM)</option>
                      </select>
                    </div>
                    <button className="w-full py-5 bg-[#1B264F] text-white rounded-xl font-black text-lg hover:bg-[#2a3a75] transition-all shadow-xl shadow-[#1B264F]/10" data-id="element-2324">
                      Request Callback
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Sign-Up Form */}
          <motion.section initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="py-16 px-8 bg-white" data-id="element-2325">
            <div className="max-w-4xl mx-auto text-center" data-id="element-2326">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#00A1E0]/10 rounded-3xl text-[#00A1E0] mb-8" data-id="element-2327">
                <Shield size={40} data-id="element-2328" />
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-[#1B264F] mb-8 tracking-tighter" data-id="element-2329">
                Ready to become a Boss?
              </h2>
              <p className="text-xl text-[#1B264F]/60 mb-12 font-medium" data-id="element-2330">
                Join 10,000+ top-performing agents who have scaled their
                business with Agenttoboss.
              </p>

              <div className="bg-[#F3F6F9] p-2 rounded-2xl border border-[#D8E2EF] flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto shadow-inner" data-id="element-2331">
                <input type="email" placeholder="Enter your work email" className="flex-1 bg-transparent px-6 py-4 text-[#1B264F] font-bold focus:outline-none placeholder:text-[#1B264F]/30" data-id="element-2332" />
                <button className="px-8 py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20 whitespace-nowrap" data-id="element-2333">
                  One-Click Sign Up
                </button>
              </div>
              <p className="mt-6 text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-2334">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </motion.section>
        </motion.div>}

      {/* Shared Footer for all storefront views */}
      <footer className="bg-[#F3F6F9] border-t border-[#D8E2EF] py-20 px-8 mt-20" data-id="element-2335">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12" data-id="element-2336">
          <div className="col-span-2 md:col-span-1" data-id="element-2337">
            <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => setView('home')} data-id="element-2338">
              <div className="w-8 h-8 bg-[#00A1E0] rounded flex items-center justify-center text-white" data-id="element-2339">
                <Zap size={18} fill="currentColor" data-id="element-2340" />
              </div>
              <span className="text-lg font-black tracking-tight text-[#1B264F]" data-id="element-2341">
                Agent<span className="text-[#00A1E0]" data-id="element-2342">to</span>boss
              </span>
            </div>
            <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-2343">
              The definitive command center for the modern enterprise agent.
              Built on the principles of scale and dominance.
            </p>
          </div>

          <div data-id="element-2344">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-2345">
              Platform
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-2346">
              <li data-id="element-2347">
                <button onClick={() => setView('crm-core')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2348">
                  CRM Core
                </button>
              </li>
              <li data-id="element-2349">
                <button onClick={() => setView('automations')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2350">
                  Automations
                </button>
              </li>
              <li data-id="element-2351">
                <button onClick={() => setView('analytics')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2352">
                  Analytics
                </button>
              </li>
              <li data-id="element-2353">
                <button onClick={() => setView('integrations')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2354">
                  Integrations
                </button>
              </li>
            </ul>
          </div>

          <div data-id="element-2355">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-2356">
              Company
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-2357">
              <li data-id="element-2358">
                <button onClick={() => setView('about')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2359">
                  About Us
                </button>
              </li>
              <li data-id="element-2360">
                <button onClick={() => setView('careers')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2361">
                  Careers
                </button>
              </li>
              <li data-id="element-2362">
                <button onClick={() => setView('press')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2363">
                  Press Kit
                </button>
              </li>
              <li data-id="element-2364">
                <button onClick={() => setView('contact')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2365">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          <div data-id="element-2366">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-2367">
              Legal
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-2368">
              <li data-id="element-2369">
                <button onClick={() => setView('privacy')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2370">
                  Privacy Policy
                </button>
              </li>
              <li data-id="element-2371">
                <button onClick={() => setView('terms')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2372">
                  Terms of Service
                </button>
              </li>
              <li data-id="element-2373">
                <button onClick={() => setView('security')} className="hover:text-[#00A1E0] transition-colors" data-id="element-2374">
                  Security
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-[#D8E2EF] flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-2375">
          <p className="text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest text-center md:text-left" data-id="element-2376">
            © 2026 Agenttoboss Inc. All rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-2377">
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-2378">
              <Twitter size={18} data-id="element-2379" />
            </a>
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-2380">
              <Linkedin size={18} data-id="element-2381" />
            </a>
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-2382">
              <Instagram size={18} data-id="element-2383" />
            </a>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence data-id="element-2384">
        {videoModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1B264F]/90 backdrop-blur-sm" onClick={() => setVideoModalOpen(false)} data-id="element-2385">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2386">
              <button onClick={() => setVideoModalOpen(false)} className="absolute top-6 right-6 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all" data-id="element-2387">
                <X size={24} data-id="element-2388" />
              </button>
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/9Qh4bHqQlnQ?autoplay=1" title="AgentToBoss Platform Overview" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-2389"></iframe>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}